/**
 * المرحلة 1.35 — a stay's two ends: «دخول إقامة» (the unit from the price
 * list, the attending doctor, the staff member in charge, the diagnosis and
 * the hour) and «الخروج» (its kind; under `accounts_clearance` it waits for
 * the accountant's «يصرح للمريض بالخروج وليس عليه أي التزامات مالية»).
 *
 * The admission opens from the child file's cover or from a board of stays;
 * the server refuses a second open stay (409 STAY_OPEN) and a discharge not
 * yet cleared (409 NOT_CLEARED with what remains).
 */

import { useEffect, useState } from 'react';
import { Button, DateTimeInput, EmptyState, Field, Icon, Input, Modal, Select, Spinner, Textarea } from '../../components/common';
import { patientsApi, staysApi } from '../../api/endpoints';
import { useNotifications } from '../../context/NotificationContext';
import { useI18n } from '../../i18n/I18nProvider';
import { formatMoney } from '../../domain/locale';
import { ageLabel } from '../../domain/childFile';
import { DISCHARGE_KINDS, admissionPayload, blankAdmission, unitChoices } from '../../domain/stays';
import { CONSENT_FOR_DISCHARGE } from '../../domain/centerForms';
import { ConsentPrints } from '../childFile/ConsentPrints';

/** The admission form for one patient. */
export function AdmitForm({ patient, onDone, onCancel }) {
  const { t } = useI18n();
  const { success, error: toastError } = useNotifications();
  const [options, setOptions] = useState(null);
  const [form, setForm] = useState(() => blankAdmission());
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    staysApi
      .options()
      .then(setOptions)
      .catch(() => setOptions({ units: [], doctors: [], staff: [] }));
  }, []);
  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));
  const save = async () => {
    setSaving(true);
    try {
      const fresh = await staysApi.admit(admissionPayload(patient.id, form));
      success(t('stay.admit.saved'));
      onDone(fresh);
    } catch (err) {
      toastError(err.body?.code === 'STAY_OPEN' ? t('stay.admit.exists') : err.message);
    } finally {
      setSaving(false);
    }
  };
  if (!options) return <Spinner label={t('stay.loading')} variant="inline" />;
  const units = unitChoices(options.units);
  return (
    <section className="sb-admit" data-testid="stay-admit-form">
      <p className="sb-admit__who">
        <Icon name="baby" size={18} /> <b>{patient.fullName}</b>
        {patient.dateOfBirth && <span> · {ageLabel(patient.dateOfBirth, t)}</span>}
      </p>
      <div className="sb-admit__grid">
        <Field label={t('stay.admit.unit')} className="sb-admit__wide">
          <Select value={form.unitItemId} onChange={set('unitItemId')} data-testid="stay-admit-unit">
            <option value="">{t('stay.admit.unitNone')}</option>
            {units.map((u) => (
              <option key={u.id} value={u.id}>
                {u.path.join(' › ')} — {formatMoney(u.price)}
              </option>
            ))}
          </Select>
        </Field>
        <Field label={t('stay.admit.doctor')}>
          <Select value={form.attendingDoctorId} onChange={set('attendingDoctorId')} data-testid="stay-admit-doctor">
            <option value="">—</option>
            {options.doctors.map((d) => (
              <option key={d.id} value={d.id}>
                {d.name}
              </option>
            ))}
          </Select>
        </Field>
        <Field label={t('stay.admit.responsible')}>
          <Select value={form.responsibleUserId} onChange={set('responsibleUserId')} data-testid="stay-admit-responsible">
            <option value="">{t('stay.admit.me')}</option>
            {options.staff.map((m) => (
              <option key={m.id} value={m.id}>
                {m.name}
              </option>
            ))}
          </Select>
        </Field>
        <Field label={t('stay.admit.admittedAt')} className="sb-admit__wide">
          <DateTimeInput value={form.admittedAt} onChange={set('admittedAt')} />
        </Field>
        <Field label={t('stay.admit.diagnosis')} className="sb-admit__wide">
          <Textarea rows={2} value={form.diagnosis} onChange={set('diagnosis')} data-testid="stay-admit-diagnosis" />
        </Field>
      </div>
      <div className="flex flex-wrap justify-end gap-2">
        {onCancel && (
          <Button variant="ghost" onClick={onCancel}>
            {t('stay.admit.cancel')}
          </Button>
        )}
        <Button icon="bed-double" loading={saving} onClick={save} data-testid="stay-admit-save">
          {t('stay.admit.save')}
        </Button>
      </div>
    </section>
  );
}

/** Find the child, then admit: the board's «إدخال طفل للإقامة». */
export function AdmitDialog({ onClose, onDone }) {
  const { t } = useI18n();
  const [q, setQ] = useState('');
  const [rows, setRows] = useState(null);
  const [picked, setPicked] = useState(null);
  useEffect(() => {
    const term = q.trim();
    if (term.length < 2) {
      setRows(null);
      return undefined;
    }
    const timer = setTimeout(() => {
      patientsApi
        .search({ q: term, limit: 12 })
        .then((r) => setRows(Array.isArray(r) ? r : []))
        .catch(() => setRows([]));
    }, 300);
    return () => clearTimeout(timer);
  }, [q]);
  return (
    <Modal open onClose={onClose} title={t('stay.board.admit')} icon="bed-double" size="lg">
      {picked ? (
        <AdmitForm patient={picked} onDone={onDone} onCancel={() => setPicked(null)} />
      ) : (
        <div className="space-y-3">
          <Field label={t('stay.board.pick')}>
            <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder={t('stay.board.search')} autoFocus data-testid="stay-admit-search" />
          </Field>
          {rows && rows.length === 0 && <EmptyState icon="search" title={t('stay.board.noMatch')} />}
          {rows && rows.length > 0 && (
            <ul className="sb-picks">
              {rows.map((p) => (
                <li key={p.id}>
                  <button type="button" className="sb-pick" onClick={() => setPicked(p)} data-testid="stay-admit-pick">
                    <Icon name="baby" size={16} />
                    <span className="sb-pick__name">{p.fullName}</span>
                    {p.dateOfBirth && <span className="sb-pick__meta">{ageLabel(p.dateOfBirth, t)}</span>}
                    {p.phonePrimary && (
                      <span className="sb-pick__meta" dir="ltr">
                        {p.phonePrimary}
                      </span>
                    )}
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </Modal>
  );
}

/** «الخروج»: the kind, and the clearance it waits for. */
export function DischargeBox({ data, onChange }) {
  const { t } = useI18n();
  const { success, error: toastError } = useNotifications();
  const [kind, setKind] = useState('');
  const [saving, setSaving] = useState(false);
  const [blocked, setBlocked] = useState(null);
  const [printing, setPrinting] = useState(false);
  const { stay, policy } = data;
  if (stay.status !== 'open') {
    // Discharged: the consent that goes with the way out, filled and ready to sign.
    const form = CONSENT_FOR_DISCHARGE[stay.dischargeKind];
    if (!form) return null;
    return (
      <section className="sb-out" data-testid="stay-discharged">
        <h4 className="sb-out__title">
          <Icon name="log-out" size={18} /> {t('stay.discharge.done', { kind: t(`stay.discharge.kind.${stay.dischargeKind}`) })}
        </h4>
        <div className="flex flex-wrap justify-end">
          <Button variant="ghost" icon="printer" onClick={() => setPrinting(true)} data-testid="stay-discharge-consent">
            {t('stay.discharge.consent', { form: t(`centerConsent.${form}.label`) })}
          </Button>
        </div>
        {printing && (
          <Modal open onClose={() => setPrinting(false)} title={t(`centerConsent.${form}.label`)} icon="file-text" size="xl">
            <ConsentPrints patient={data.patient} stay={stay} defaultForm={form} />
          </Modal>
        )}
      </section>
    );
  }
  const waits = policy.discharge === 'accounts_clearance' && !stay.clearedAt;
  const save = async () => {
    setSaving(true);
    setBlocked(null);
    try {
      const fresh = await staysApi.discharge(stay.id, kind);
      success(t('stay.discharge.saved'));
      onChange(fresh);
    } catch (err) {
      if (err.body?.code === 'NOT_CLEARED') setBlocked(err.body.remaining ?? null);
      else toastError(err.message);
    } finally {
      setSaving(false);
    }
  };
  return (
    <section className="sb-out" data-testid="stay-discharge">
      <h4 className="sb-out__title">
        <Icon name="log-out" size={18} /> {t('stay.discharge.title')}
      </h4>
      <p className={`sb-out__state ${waits ? 'sb-out__state--wait' : 'sb-out__state--ok'}`}>
        <Icon name={waits ? 'clock' : 'badge-check'} size={16} />
        {waits ? t('stay.discharge.waitClear') : policy.discharge === 'free' ? t('stay.discharge.free') : t('stay.discharge.cleared', { name: stay.clearedName || '' })}
      </p>
      {blocked !== null && (
        <p className="sb-out__blocked" role="alert" data-testid="stay-not-cleared">
          {t('stay.discharge.notCleared', { amount: formatMoney(blocked) })}
        </p>
      )}
      {data.can.discharge && (
        <div className="sb-out__form">
          <Field label={t('stay.discharge.kindLabel')}>
            <Select value={kind} onChange={(e) => setKind(e.target.value)} data-testid="stay-discharge-kind">
              <option value="">—</option>
              {DISCHARGE_KINDS.map((k) => (
                <option key={k} value={k}>
                  {t(`stay.discharge.kind.${k}`)}
                </option>
              ))}
            </Select>
          </Field>
          <Button variant="danger" icon="log-out" loading={saving} disabled={!kind} onClick={save} data-testid="stay-discharge-save">
            {t('stay.discharge.save')}
          </Button>
        </div>
      )}
    </section>
  );
}
